import React, { useState, useEffect, Fragment } from 'react';
import { graphql } from 'react-apollo';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';
import moment from 'moment';
import qs from 'query-string';

import BoxedArrowLink from '../../../../components/global/BoxedArrowLink';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  accountLogin,
  accountAddRoles,
  clientStoreSet as clientStoreSetMutation,
  emailAuthenticationRequest as emailAuthenticationRequestMutation,
} from '../../../../graphql/mutations/account';
import { MeQuery } from '../../../../graphql/queries/account';
import ACCOUNT_ROLES from '../../../../constants/account-roles';
import { loginSuccess as loginSuccessAction } from '../../../../actions/auth-actions';
import {
  ContributorInvitationDetails,
  ContributorAssignmentShare,
} from '../../../../graphql/queries/contributors';
import { isEmailLoginOnly } from '../../../../util/contributor-assignments';
import { isProAccount } from '../../../../util/account';
import getConfig from '../../../../util/config';
import SignupAlbumLayout from '../../../../components/global/SignupAlbumLayout';
import BlackbaudLogin from '../../../../components/global/BlackbaudLogin/BlackbaudLogin';
import HeadOpenGraphMeta from '../../../../components/global/HeadOpenGraphMeta';
import { USER_MODE, AUTH_MODE, CONTENT_PANE } from './constants';
import PressLogos from './components/PressLogos';
import SignInMethodToggles from './components/SignInMethodToggles';
import AuthModePhone from './components/AuthModePhone';
import AuthModeEmailPassword from './components/AuthModeEmailPassword';
import AuthModeEmailLink from './components/AuthModeEmailLink';

const propTypes = {
  login: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  addShutterbugRole: PropTypes.func.isRequired,
  loginSuccess: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  contributorInvitationDetails: PropTypes.shape({
    token: PropTypes.string,
    email: PropTypes.string,
    shareId: PropTypes.string,
  }),
  clientStoreSet: PropTypes.func,
  emailAuthenticationRequest: PropTypes.func.isRequired,
  contributorAssignmentShare: PropTypes.shape({
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

const Login = ({
  login,
  user,
  addShutterbugRole,
  loginSuccess,
  history,
  contributorInvitationDetails,
  clientStoreSet,
  emailAuthenticationRequest,
  contributorAssignmentShare,
}) => {
  const { search } = useLocation();
  const {
    next,
    defaultUserMode,
    defaultSignupDisabled = 0,
    methodsDisabled = 0,
    ...queryParams
  } = qs.parse(search);

  const [authMode, setAuthMode] = useState(AUTH_MODE.PHONE);
  const [contentPane, setContentPane] = useState(CONTENT_PANE.LOGIN_FORM);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // userMode is only a visual representation to whether you're logging in
  // as a shutterbug and/or a PRO user. It doesn't really affect the end-result at all
  // since a login is a login regardless of how you do it. Perhaps in the future this can be used
  // to determine where to land the user for PRO+Shutterbug users.
  const [userMode, setUserMode] = useState(
    defaultUserMode ?? USER_MODE.SHUTTERBUG,
  );

  const [loading, setLoading] = useState(false);
  const [isFormValid, setFormValidity] = useState(false);
  const [error, setError] = useState('');
  const [hideSignInMethodToggles, setHideSignInMethodToggles] = useState(
    methodsDisabled !== 0,
  );
  const [signupDisabled, setSignupDisabled] = useState(
    defaultSignupDisabled !== 0,
  );

  const { oauth } = getConfig();

  useEffect(() => {
    if (userMode === USER_MODE.SHUTTERBUG) {
      setAuthMode(AUTH_MODE.PHONE);
    } else {
      setAuthMode(AUTH_MODE.EMAIL_PASSWORD);
    }
  }, [userMode]);

  useEffect(() => {
    if (contributorAssignmentShare) {
      const isEmailLoginOnlyBool = isEmailLoginOnly(contributorAssignmentShare);

      setAuthMode(
        isEmailLoginOnlyBool ? AUTH_MODE.EMAIL_PASSWORD : AUTH_MODE.PHONE,
      );
      setSignupDisabled(isEmailLoginOnlyBool);
      setHideSignInMethodToggles(isEmailLoginOnlyBool);
    }
  }, [contributorAssignmentShare]);

  const onUserLoggedIn = async () => {
    const shouldAddShutterbugRole = !isProAccount(user);

    if (
      shouldAddShutterbugRole &&
      !user.roles.includes(ACCOUNT_ROLES.SHUTTERBUG)
    ) {
      // Set the time the user signed up, and forget (no need to await).
      clientStoreSet?.({
        account_id: user.id,
        key: 'shutterbug_signup_timestamp',
        data: moment().valueOf().toString(),
      });

      await addShutterbugRole(user.id);
    }

    if (contributorInvitationDetails) {
      history.push('/contributors/invres');
      return;
    }

    history.push(next || '/app');
  };

  useEffect(() => {
    setFormValidity(email !== '' && password !== '');
  }, [email, password]);

  useEffect(() => {
    if (user) {
      onUserLoggedIn();
    }
  }, [user]);

  const attemptEmailPasswordLogin = async () => {
    setLoading(true);
    setError('');

    try {
      await login(email, password);

      // push in to state, so user/meQuery reruns and let user useEffect above 'continue' login flow
      loginSuccess();
    } catch (err) {
      setError(getErrorString(err));
      setLoading(false);
    }
  };

  const initiateEmailAuth = async event => {
    // TODO: If we ever implement back Magic link, it NEEDS to support
    // contributor invitation acceptance (which it doesn't atm). Basically add the
    // Redirect path similar to what's done in Signup.
    if (event) {
      event.preventDefault();
    }
    setLoading(true);
    await emailAuthenticationRequest({ email });
    setLoading(false);
    setContentPane(CONTENT_PANE.EMAIL_INSTRUCTIONS);
  };

  const resendVerificationEmail = async () => {
    await emailAuthenticationRequest({ email });
  };

  return (
    <Fragment>
      <HeadOpenGraphMeta
        title="Login"
        description="Private photo & video sharing"
      />
      <SignupAlbumLayout.Wrapper
        withCTA={!signupDisabled}
        buttonText="Create an account"
        buttonTo={
          userMode === USER_MODE.PRO ? '/auth/register' : '/shutterbug/register'
        }
        imageContainerClassName={
          userMode === USER_MODE.PRO
            ? 'shutterbug-login-email-layout-image'
            : undefined
        }
      >
        <div className="shutterbug-login">
          <h3>
            Welcome back! <br /> Sign in here.
          </h3>
          <div className="login-form">
            {contentPane === CONTENT_PANE.LOGIN_FORM && (
              <Fragment>
                <SignInMethodToggles
                  userMode={userMode}
                  setUserMode={setUserMode}
                  hidden={
                    hideSignInMethodToggles || authMode === AUTH_MODE.EMAIL_LINK
                  }
                />
                <div>
                  {authMode === AUTH_MODE.PHONE && (
                    <AuthModePhone
                      defaultPhone={
                        queryParams.phone || queryParams.phoneNumber
                      }
                      onSentPin={() => {
                        setHideSignInMethodToggles(true);
                      }}
                      onEmailAuth={() => {
                        setAuthMode(AUTH_MODE.EMAIL_PASSWORD);
                      }}
                      allowEmailAuth={!hideSignInMethodToggles}
                    />
                  )}

                  {authMode === AUTH_MODE.EMAIL_LINK && (
                    <AuthModeEmailLink
                      values={{ email }}
                      onChange={(key, value) => {
                        if (key === 'email') {
                          setEmail(value);
                        }
                      }}
                      onInitiateEmailAuth={initiateEmailAuth}
                      onBackToPhone={() => {
                        setAuthMode(AUTH_MODE.PHONE);
                      }}
                    />
                  )}

                  {authMode === AUTH_MODE.EMAIL_PASSWORD && (
                    <AuthModeEmailPassword
                      onSubmit={event => {
                        event.preventDefault();
                        attemptEmailPasswordLogin();
                      }}
                      values={{
                        email,
                        password,
                      }}
                      onChange={(key, value) => {
                        if (key === 'email') {
                          setEmail(value);
                        } else if (key === 'password') {
                          setPassword(value);
                        }
                      }}
                      error={error}
                      loading={loading}
                      isFormValid={isFormValid}
                      onSendMagicLink={() => {
                        setAuthMode(AUTH_MODE.EMAIL_LINK);
                      }}
                    />
                  )}
                </div>
              </Fragment>
            )}

            {contentPane === CONTENT_PANE.EMAIL_INSTRUCTIONS && (
              <Fragment>
                <h2 className="signup-header">Please verify your email</h2>
                <p>
                  Striving for a secure experience with Waldo, we&apos;ve sent a
                  link to verify your email.
                </p>
                <p>
                  Please click on the link we&apos;ve sent to{' '}
                  <a href="#">{email}</a> to continue.
                </p>
                <h4>Didn&apos;t receive a verification link?</h4>

                <BoxedArrowLink onClick={resendVerificationEmail}>
                  Re-send verification link
                </BoxedArrowLink>
              </Fragment>
            )}

            {oauth?.blackbaud &&
              authMode === AUTH_MODE.EMAIL_PASSWORD &&
              userMode === USER_MODE.PRO && (
                <BlackbaudLogin
                  className="login-email-form-button email-signin-button"
                  btnSize="sm"
                  btnOutline
                >
                  Sign in with Blackbaud
                </BlackbaudLogin>
              )}
          </div>
          <PressLogos className="mt-3" />
        </div>
      </SignupAlbumLayout.Wrapper>
    </Fragment>
  );
};

Login.propTypes = propTypes;

export { Login as LoginComponent };

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = {
  loginSuccess: loginSuccessAction,
};

const withLoginMutation = graphql(accountLogin, {
  props: ({ mutate }) => ({
    login: (email, password) =>
      mutate({
        variables: {
          input: { email, password },
        },
      }),
  }),
});

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ownProps => !ownProps.authenticated,
});

const withAccountAddRolesMutation = graphql(accountAddRoles, {
  props: ({ mutate }) => ({
    addShutterbugRole: accountId =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            // note: ACCOUNT_ROLES enum contains lowercase, but API requires uppercase
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      }),
  }),
});

const withContributorInvitationDetails = graphql(ContributorInvitationDetails, {
  props: ({ data: { contributorInvitationDetails } }) => ({
    contributorInvitationDetails,
  }),
});

const withContributorAssignmentShare = graphql(ContributorAssignmentShare, {
  options: ({ contributorInvitationDetails = {} }) => {
    const { shareId } = contributorInvitationDetails;

    return {
      variables: {
        id: shareId,
      },
    };
  },
  props: ({ data: { contributorAssignmentShare } }) => ({
    contributorAssignmentShare,
  }),
  skip: ({ contributorInvitationDetails = {} }) => {
    const { shareId } = contributorInvitationDetails;
    return !shareId;
  },
});

const withClientStoreSetMutation = graphql(clientStoreSetMutation, {
  props: ({ mutate }) => ({
    clientStoreSet: input =>
      mutate({
        variables: {
          input,
        },
      }),
  }),
});

const withEmailAuthenticationMutation = graphql(
  emailAuthenticationRequestMutation,
  {
    props: ({ mutate }) => ({
      emailAuthenticationRequest: ({ email }) =>
        mutate({
          variables: {
            input: {
              email_address: email,
              intent: 'LOGIN',
              redirect_path: '/app',
            },
          },
        }),
    }),
  },
);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLoginMutation,
  withUser,
  withContributorInvitationDetails,
  withContributorAssignmentShare,
  withAccountAddRolesMutation,
  withClientStoreSetMutation,
  withEmailAuthenticationMutation,
)(Login);
